import React, { useMemo, useState } from 'react';
import { Card, Select, Space, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EyeOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useGetQaqcPipeWps } from '../../../../hooks/genericHooks';
import StandardTable from '../../../../components/common/StandardTable';
import type { QaqcPipeWps } from '../../../../types';
import Button from '../../../../components/common/Button';

const QaqcPipeWpsList: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetQaqcPipeWps();
  const rows: QaqcPipeWps[] = Array.isArray(data?.data) ? (data!.data as unknown as QaqcPipeWps[]) : [];

  // Filters state
  const [filterWps, setFilterWps] = useState<string | undefined>();
  const [filterOs1, setFilterOs1] = useState<string | undefined>();
  const [filterSvc, setFilterSvc] = useState<string[] | undefined>();

  // Options for selects
  const wpsOptions = useMemo(() => Array.from(new Set(rows.map(r => r.wps_name))).sort()
    .map(v => ({ value: v, label: v })), [rows]);

  const os1Options = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.wps_oversize1) set.add(r.wps_oversize1); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [rows]);

  const svcOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => String(r.serviceclass_list||'').split(',').map(s=>s.trim()).filter(Boolean).forEach(s=>set.add(s)));
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filterWps && r.wps_name !== filterWps) return false;
      if (filterOs1 && (r.wps_oversize1 || '') !== filterOs1) return false;
      if (filterSvc && filterSvc.length > 0) {
        const list = String(r.serviceclass_list||'').split(',').map(s=>s.trim()).filter(Boolean);
        if (!filterSvc.every(s => list.includes(s))) return false;
      }
      return true;
    });
  }, [rows, filterWps, filterOs1, filterSvc]);

  const columns = useMemo(
    () => [
      { title: 'WPS', dataIndex: 'wps_name', key: 'wps_name' },
      { title: 'Service Classes', dataIndex: 'serviceclass_list', key: 'serviceclass_list' },
      { title: 'Oversize 1', dataIndex: 'wps_oversize1', key: 'wps_oversize1' },
      { title: 'OS1 Classes', dataIndex: 'serviceclass_list_os1', key: 'serviceclass_list_os1' },
      { title: 'Oversize 2', dataIndex: 'wps_oversize2', key: 'wps_oversize2' },
      { title: 'OS2 Classes', dataIndex: 'serviceclass_list_os2', key: 'serviceclass_list_os2' },
      { title: 'Updated By', dataIndex: 'updated_by', key: 'updated_by' },
      { title: 'Updated At', dataIndex: 'updated_at', key: 'updated_at' },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        fixed: 'right',
        render: (_: any, rec: QaqcPipeWps) => (
          <Space>
            <Tooltip title="View">
              <Button action="read" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/qaqc/piping/tools/wps/view/${rec.id}`, { state: { record: rec } })} />
            </Tooltip>
            <Tooltip title="Edit">
              <Button action="edit" size="small" type="primary" icon={<EditOutlined />} onClick={() => navigate(`/qaqc/piping/tools/wps/edit/${rec.id}`, { state: { record: rec } })} />
            </Tooltip>
          </Space>
        )
      }
    ],
    []
  );

  return (
    <Card
      title="WPS List"
      extra={
        <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qaqc/piping/tools/wps/create')}>
          New WPS
        </Button>
      }
    >
      {error ? (
        <div style={{ color: 'red', marginBottom: 12 }}>Failed to load WPS: {String(error.message || error)}</div>
      ) : null}
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          showSearch
          placeholder="Filter by WPS"
          style={{ width: 220 }}
          options={wpsOptions}
          value={filterWps}
          onChange={setFilterWps}
          optionFilterProp="label"
        />
        <Select
          allowClear
          showSearch
          placeholder="Filter by Oversize 1"
          style={{ width: 220 }}
          options={os1Options}
          value={filterOs1}
          onChange={setFilterOs1}
          optionFilterProp="label"
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by Service Class"
          style={{ minWidth: 320 }}
          options={svcOptions}
          value={filterSvc}
          onChange={setFilterSvc}
          optionFilterProp="label"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            setFilterWps(undefined);
            setFilterOs1(undefined);
            setFilterSvc(undefined);
          }}
        >
          Reset Filters
        </Button>
      </Space>
      <StandardTable
        loading={isLoading}
        columns={columns as any}
        dataSource={filtered}
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </Card>
  );
};

export default QaqcPipeWpsList;
